import { focusPresentedFramesInDesktopMode } from "@/lib/focus/focus-desktop-mode";
import { commitPresentation } from "@/lib/focus/focus-adapter";
import { currentFocusPlanEnv } from "@/lib/focus/focus-natural-size";
import { planDismissFocusFrame } from "@/lib/focus/focus-plan";
import { exitPresentationMode } from "@/lib/presentation-mode";
import { isTransientConversationFrameMetadata } from "@/lib/transient-conversation-frame";
import { useFramesStore } from "@/stores/frames-store";
import { usePresentationStore } from "@/stores/presentation-store";

/**
 * Dismiss a persistent frame from the temporary Focus layout without changing
 * its durable workspace visibility. Transient conversation frames keep their
 * existing close/delete semantics and are deliberately not handled here.
 */
export function dismissFrameFromFocusPresentation(frameId: string): boolean {
  const frame = useFramesStore.getState().framesById[frameId];
  if (!frame || isTransientConversationFrameMetadata(frame.metadata)) {
    return false;
  }
  return removeFrameFromFocusPresentation(frameId);
}

/**
 * Take any frame, a transient chat included, out of the Focus layout: its
 * window now lives elsewhere (a floating window on another display). A
 * transient chat is still discarded when Focus ends.
 */
export function removeFrameFromFocusPresentation(frameId: string): boolean {
  const presentation = usePresentationStore.getState().presentation;
  if (
    presentation?.variant !== "focus" ||
    !useFramesStore.getState().framesById[frameId]
  ) {
    return false;
  }
  const dismissal = planDismissFocusFrame(
    presentation,
    frameId,
    currentFocusPlanEnv(),
  );
  if (!dismissal) return false;
  if (dismissal.kind === "exit") {
    exitPresentationMode();
    return true;
  }
  commitPresentation(dismissal.next);
  focusPresentedFramesInDesktopMode(dismissal.next.frameIds);
  return true;
}
